import { cachedPerformance, remember, rememberSegment } from "./cache";
import type { ApiClient, EventHandlers } from "./client";
import { ApiError, NotConnectedError } from "./errors";
import { type OperationId, inContract } from "./operations";
import { scrubPost } from "./redaction";
import type {
  HistoryEntry,
  HistoryFilter,
  Id,
  Me,
  Moment,
  MomentDetail,
  MomentEdit,
  Page,
  Performance,
  Performer,
  Playback,
  Playlist,
  Post,
  QueueItem,
  QueueSnapshot,
  Report,
  Saved,
  SearchResult,
  Segment,
  Session,
  Work,
} from "./types";

/**
 * 契約（contracts/openapi.json）の API を使って、画面が必要とする形を組み立てる共通部分。
 * 通信のしかただけを、本物（src/api/http.ts）と開発用モック（src/mocks/mockApi.ts）が受け持つ。
 * モックも同じ組み立てを通るので、契約で足りないもの（★）はモックでも同じように足りなく見える。
 * 契約に無い操作は、通信せずに NotConnectedError にする（まだ無い API を呼んで成功したように見せない）。
 */

export type Query = Record<string, string | number | boolean | undefined | null | string[]>;

export interface RequestOptions {
  params?: Record<string, string>;
  query?: Query;
  body?: unknown;
}

export function toQuery(query: Query | undefined): string {
  if (!query) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((item) => sp.append(k, item));
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** 一覧は1ページ20件。has_more が false になるか、この上限まで集める */
const MAX_PAGES = 10;

/** 公開投稿に付いたここ好きを、投稿の持ち主の名前つきで取り出す */
export function publicMoments(posts: Post[]): Moment[] {
  return posts
    .filter((p) => !p.unavailable && p.visibility === "members")
    .flatMap((p) => (p.moments ?? []).map((m) => ({ ...m, author_name: p.author_name, visibility: "members" as const })));
}

function keep<T extends Performance | null | undefined>(p: T): T {
  remember(p);
  return p;
}

function cleanItem(item: QueueItem): QueueItem {
  keep(item.performance);
  return { ...item, post: scrubPost(item.post) };
}

export abstract class ContractClient implements ApiClient {
  abstract readonly mode: ApiClient["mode"];

  /** 契約の操作を一つ呼ぶ。契約に無い操作は呼ばずに NotConnectedError */
  protected abstract send<T>(op: OperationId, opts: RequestOptions): Promise<T>;
  abstract subscribe(handlers: EventHandlers): () => void;
  abstract loginUrl(): string | null;
  /** /me を受け取ったとき（CSRF の控えなど） */
  protected onMe(_me: Me): void {}

  isConnected(op: OperationId): boolean {
    return inContract(op);
  }

  protected request<T>(op: OperationId, opts: RequestOptions = {}): Promise<T> {
    if (!this.isConnected(op)) return Promise.reject(new NotConnectedError(op));
    return this.send<T>(op, opts);
  }

  private async all<T>(op: OperationId, query: Query = {}, maxPages = MAX_PAGES, params?: Record<string, string>): Promise<T[]> {
    const out: T[] = [];
    for (let page = 1; page <= maxPages; page += 1) {
      const p = await this.request<Page<T>>(op, { params, query: { ...query, page } });
      out.push(...p.items);
      if (!p.has_more) break;
    }
    return out;
  }

  /** 投稿に歌唱を付ける（契約は performance_id だけなので、手元の控えか GET /performances/{id} で引く） */
  private async attachPerformances(posts: Post[]): Promise<Post[]> {
    posts.forEach((p) => keep(p.performance));
    const missing = [...new Set(posts.map((p) => p.performance_id))].filter((id) => !cachedPerformance(id));
    await Promise.all(
      missing.map((id) =>
        this.request<Performance>("performances.get", { params: { id } })
          .then(keep)
          .catch(() => undefined),
      ),
    );
    return posts.map((p) => {
      const clean = scrubPost(p);
      return { ...clean, moments: clean.moments ?? [], performance: p.performance ?? cachedPerformance(p.performance_id) };
    });
  }

  async getPosts(page = 1): Promise<Page<Post>> {
    const result = await this.request<Page<Post>>("posts.list", { query: { page } });
    return { ...result, items: await this.attachPerformances(result.items) };
  }

  /* ------------------------------------------------------------ ログイン */

  async getSession(): Promise<Session> {
    if (!this.isConnected("me")) {
      return { status: "unavailable", code: "not_connected", message: "ログイン状態のAPIがまだ接続されていません。" };
    }
    try {
      const me = await this.request<Me>("me");
      this.onMe(me);
      return { status: "signed_in", me };
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401) return { status: "signed_out" };
        if (e.status === 403 && (e.code === "membership_required" || e.code === "wrong_guild")) return { status: "not_member" };
        return { status: "unavailable", code: e.code, message: e.message };
      }
      return { status: "unavailable", code: "network", message: "" };
    }
  }

  async logout() {
    await this.request<void>("logout");
  }

  /* ------------------------------------------------------------ 再生 */

  async getPlayback() {
    const p = await this.request<Playback>("playback");
    return { ...p, current: p.current ? cleanItem(p.current) : null };
  }

  async getQueue() {
    const q = await this.request<QueueSnapshot>("queue");
    return { ...q, items: q.items.map(cleanItem) };
  }

  postCommand: ApiClient["postCommand"] = (input) => this.request("commands.create", { body: input });
  getCommand: ApiClient["getCommand"] = (id) => this.request("commands.get", { params: { id } });

  /* ------------------------------------------------------------ ここ好き */

  captureMoment: ApiClient["captureMoment"] = (input) => this.request("moments.capture", { body: input });

  async getMoment(id: Id): Promise<MomentDetail> {
    const raw = await this.request<Moment>("moments.get", { params: { id } });
    return {
      moment: raw,
      performance: raw.performance ?? null,
      segment: raw.segment ?? raw.performance?.segment ?? {
        id: raw.segment_revision_id, start_ms: raw.source_position_ms - raw.position_ms,
      },
    };
  }

  editMoment: ApiClient["editMoment"] = (id, edit) =>
    this.request("moments.edit", { params: { id }, body: { ...edit, visibility: "private" } satisfies MomentEdit });

  publishMoment: ApiClient["publishMoment"] = (id, edit) =>
    this.request("moments.edit", { params: { id }, body: { ...edit, visibility: "members" } satisfies MomentEdit });

  deleteMoment: ApiClient["deleteMoment"] = (id) => this.request("moments.delete", { params: { id } });

  /* ------------------------------------------------------------ 見つける・検索 */

  async getDiscover() {
    const [posts, unplayed] = await Promise.all([
      this.request<Page<Post>>("posts.list", { query: { page: 1 } }),
      this.request<Page<Performance>>("performances.list", { query: { feed: "unplayed", page: 1 } }),
    ]);
    unplayed.items.forEach(keep);
    const withPerf = await this.attachPerformances(posts.items);
    const visible = withPerf.filter((p) => !p.unavailable && p.visibility === "members");
    return {
      posts: visible,
      unplayed: unplayed.items,
      moments: visible.flatMap((p) => (p.performance ? publicMoments([p]).map((m) => ({ ...m, performance: m.performance ?? p.performance! })) : [])),
    };
  }

  async search(q: string, page = 1): Promise<SearchResult> {
    const r = await this.request<SearchResult>("catalog.search", { query: { q, page, include_videos: true } });
    r.performances.items.forEach(keep);
    return r;
  }

  inspectVideo: ApiClient["inspectVideo"] = (url) => this.request("catalog.inspect", { query: { url } });

  async findByVideo(videoId: string) {
    const r = await this.request<Page<Performance>>("performances.list", { query: { video_id: videoId, page: 1 } });
    return r.items.map(keep);
  }

  /* ------------------------------------------------------------ 曲・歌唱 */

  async searchWorks(q: string) {
    return (await this.request<Page<Work>>("works.search", { query: { q, page: 1 } })).items;
  }

  createWork: ApiClient["createWork"] = (input) => this.request("works.create", { body: input });

  async getWork(id: Id) {
    const [work, performances] = await Promise.all([
      this.request<Work>("works.get", { params: { id } }),
      this.all<Performance>("performances.list", { work_id: id }),
    ]);
    performances.forEach(keep);
    return { work, performances };
  }

  async searchPerformers(q: string) {
    return (await this.request<Page<Performer>>("performers.search", { query: { q, page: 1 } })).items;
  }

  createPerformer: ApiClient["createPerformer"] = (input) => this.request("performers.create", { body: input });
  createPerformance: ApiClient["createPerformance"] = async (input) =>
    keep(await this.request<Performance>("performances.create", { body: input }));

  async getPerformance(id: Id) {
    const [performance, posts] = await Promise.all([
      this.request<Performance>("performances.get", { params: { id } }),
      this.isConnected("posts.list")
        ? this.request<Page<Post>>("posts.list", { query: { performance_id: id, page: 1 } })
        : Promise.resolve(null),
    ]);
    keep(performance);
    const siblings = this.isConnected("performances.list")
      ? (await this.all<Performance>("performances.list", { work_id: performance.work.id }, 3)).filter((p) => p.id !== id).map(keep)
      : [];
    const visible = (posts?.items ?? [])
      .map((p) => {
        const clean = scrubPost(p);
        return { ...clean, moments: clean.moments ?? [], performance };
      })
      .filter((p) => !p.unavailable && p.visibility === "members");
    return {
      performance,
      posts: visible,
      moments: publicMoments(visible).sort((a, b) => a.position_ms - b.position_ms),
      siblings,
    };
  }

  editPerformance: ApiClient["editPerformance"] = async (id, edit) =>
    keep(await this.request<Performance>("performances.edit", { params: { id }, body: edit }));

  addSegmentRevision: ApiClient["addSegmentRevision"] = async (id, input) => {
    const seg = await this.request<Segment>("segments.create", { params: { id }, body: input });
    rememberSegment(id, seg);
    return seg;
  };

  async getPerformances(ids: Id[]) {
    const found = await Promise.all(
      ids.map((id) =>
        this.request<Performance>("performances.get", { params: { id } })
          .then(keep)
          .catch(() => null),
      ),
    );
    return found.filter((p): p is Performance => p !== null);
  }

  /* ------------------------------------------------------------ 投稿 */

  getPost: ApiClient["getPost"] = async (id) => {
    const post = await this.request<Post>("posts.get", { params: { id } });
    return (await this.attachPerformances([post]))[0];
  };
  createPost: ApiClient["createPost"] = (input) => this.request("posts.create", { body: input });
  editPost: ApiClient["editPost"] = (id, edit) => this.request("posts.edit", { params: { id }, body: edit });
  deletePost: ApiClient["deletePost"] = (id) => this.request("posts.delete", { params: { id } });

  /* ------------------------------------------------------------ ライブラリ */

  async listSaved() {
    const items = await this.all<Saved>("library", { kind: "all" });
    return items.map((s) => {
      keep(s.performance);
      return { ...s, post: scrubPost(s.post) };
    });
  }

  async getLibrary() {
    const [saved, moments, posts] = await Promise.all([
      this.listSaved(),
      this.isConnected("moments.list") ? this.all<Moment>("moments.list") : Promise.resolve([] as Moment[]),
      this.isConnected("posts.list") ? this.all<Post>("posts.list", { mine: true }) : Promise.resolve([] as Post[]),
    ]);
    const myPosts = await this.attachPerformances(posts);
    return {
      saved_performances: saved.filter((s) => !s.post),
      saved_posts: saved.filter((s) => !!s.post).map((s) => ({ ...s, post: s.post ? { ...s.post, performance: s.performance } : null })),
      moments: moments.map((m) => ({
        ...m,
        performance: m.performance ?? null,
      })),
      posts: myPosts,
    };
  }

  save: ApiClient["save"] = (target) => this.request("library.save", { body: target });
  unsave: ApiClient["unsave"] = (savedId) => this.request("library.unsave", { params: { id: savedId } });
  getPlaylists = () => this.all<Playlist>("playlists.list");
  getPlaylist: ApiClient["getPlaylist"] = (id) => this.request("playlists.get", { params: { id } });
  createPlaylist: ApiClient["createPlaylist"] = (name) => this.request("playlists.create", { body: { name } });
  editPlaylist: ApiClient["editPlaylist"] = (id, edit) => this.request("playlists.edit", { params: { id }, body: edit });
  deletePlaylist: ApiClient["deletePlaylist"] = (id) => this.request("playlists.delete", { params: { id } });

  /* ------------------------------------------------------------ 履歴 */

  async getHistory(filter: HistoryFilter) {
    let after: string | undefined;
    let before: string | undefined;
    if (filter.date) {
      // 見ている人の時間帯の1日を、UTC の範囲にして送る
      const start = new Date(`${filter.date}T00:00:00`);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      after = start.toISOString();
      before = end.toISOString();
    }
    const page = await this.request<Page<HistoryEntry>>("history", { query: { q: filter.q, after, before, page: filter.page ?? 1 } });
    return { ...page, items: page.items.map((h) => ({ ...h, item: cleanItem(h.item) })) };
  }

  /* ------------------------------------------------------------ 通報・運営 */

  report: ApiClient["report"] = (input) => this.request("reports.create", { body: input });

  async getReports() {
    const reports = await this.all<Report>("reports.list", {}, 3);
    return Promise.all(
      reports.map(async (r) => {
        const post = r.post !== undefined ? r.post : this.isConnected("posts.get")
          ? await this.request<Post>("posts.get", { params: { id: r.post_id } }).catch(() => null)
          : null;
        return { ...r, post: post ? scrubPost(post) : null };
      }),
    );
  }

  moderateReport: ApiClient["moderateReport"] = (reportId, hide) =>
    this.request("reports.moderate", { params: { id: reportId }, body: { hide } });
}
